import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import cn from 'classnames';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { TextField } from 'uiKit/atoms/TextField/TextField';
import { useStores } from 'stores';
import { IconButton } from 'uiKit/atoms/IconButton/IconButton';
import { List } from 'uiKit/atoms/List/List';
import { ListItem } from 'uiKit/atoms/ListItem/ListItem';
import { TelegramIcon } from 'uiKit/atoms/TelegramIcon/TelegramIcon';
import ChatCard from './ChatCard/ChatCard';
import ChatWindow from './ChatWindow/ChatWindow';
import ConversationHeader from './ConversationHeader';
import { RequiredFieldString } from '../../constants';
import styles from './Conversations.module.scss';
import { LOT_STATUS, MAX_LENGTH } from 'constants/common';
import { stripInvisibleChars } from './utils';

const MessageSchema = Yup.object().shape({
  text: Yup.string().trim().max(2000, MAX_LENGTH).required(RequiredFieldString),
});

const initialValues = {
  text: '',
};

const Conversations = () => {
  const {
    messageStore: {
      getConversations,
      clearConversationState,
      setConversation,
      sendMessage,
      currentConversation,
      conversations,
    },
  } = useStores();
  const navigate = useNavigate();
  const formikRef = useRef();
  const { id } = useParams();
  const [isMessageChanged, setMessageChagned] = useState(false);
  const [isDeletedLot, setIsDeletedLot] = useState(false);
  let newConversationData = {};

  const resetMsgForm = () => formikRef.current?.resetForm();

  useEffect(() => {
    getConversations();

    return () => {
      clearConversationState();
    };
  }, []);

  useEffect(() => {
    setConversation(id);
    resetMsgForm();
  }, [id, setConversation]);

  const getOpenConversation = () => {
    newConversationData = JSON.parse(sessionStorage.getItem('newConversationData'));

    if (!newConversationData) {
      return null;
    }

    return <ChatCard key={newConversationData.id} conversation={newConversationData} />;
  };

  useEffect(() => {
    if (currentConversation && conversations.length > 0) {
      const lotDeleted = conversations.some(
        chat => chat.id === currentConversation && chat.lotStatus === LOT_STATUS.DELETED
      );
      setIsDeletedLot(lotDeleted);
    }
  }, [currentConversation, conversations]);

  const handleMessageSubmit = async (values, { resetForm }) => {
    const msgData = {
      text: values.text,
    };

    if (msgData.text.length <= 2000) {
      if (Number(currentConversation) !== -1) {
        msgData.conversationId = Number(currentConversation);
      } else {
        msgData.lotId = newConversationData.lotId;
      }

      // Disable the send button to prevent multiple submissions of the same message.
      setMessageChagned(false);

      if (sessionStorage.getItem(`draftMsg${currentConversation}`)) {
        sessionStorage.removeItem(`draftMsg${currentConversation}`);
      }

      await sendMessage(msgData, navigate);

      resetForm({});
    }
  };

  return (
    <main className={styles['flex-container']}>
      <div className={styles.bns3_main_container}>
        <h1 className={styles.heading}>My Messages</h1>
      </div>
      <div className={styles.chat}>
        <div className={cn(styles.left, 'col-sm-4')}>
          <List className={styles['chats-block']} component="nav" aria-labelledby="nested-list-subheader">
            <ListItem>
              <div className={styles['chats-block-title']}>Chats</div>
            </ListItem>
            <div className={styles['chats-block-content']}>
              {getOpenConversation()}
              {conversations.map(chat => (
                <ChatCard component={ListItem} button conversation={chat} key={chat.id} />
              ))}
            </div>
          </List>
        </div>
        <div className={cn(styles.right, 'col-sm-8')}>
          <ConversationHeader />
          <div className={styles.chatBox}>
            <ChatWindow />
          </div>
          <div>
            {!isDeletedLot && !!(currentConversation && currentConversation !== 0) && (
              <Formik
                initialValues={initialValues}
                validationSchema={MessageSchema}
                enableReinitialize
                innerRef={formikRef}
                onSubmit={handleMessageSubmit}
                validateOnChange
              >
                {({ values, errors, touched, handleSubmit, setFieldValue }) => {
                  const handleMessage = e => {
                    const val = e.target.value;
                    setFieldValue('text', val);
                    const draftMsg = { message: val, conversationId: currentConversation };
                    sessionStorage.setItem(`draftMsg${currentConversation}`, JSON.stringify(draftMsg));
                    if (isDeletedLot || !stripInvisibleChars(val)) {
                      sessionStorage.removeItem(`draftMsg${currentConversation}`);
                      setMessageChagned(false);
                    } else {
                      setMessageChagned(true);
                    }
                  };

                  useEffect(() => {
                    const draftMsg = sessionStorage.getItem(`draftMsg${currentConversation}`);
                    if (draftMsg && values.text !== JSON.parse(draftMsg).message) {
                      const message = JSON.parse(draftMsg).message;
                      setFieldValue('text', message);
                      if (!isDeletedLot && stripInvisibleChars(message)) {
                        setMessageChagned(true);
                      } else {
                        setMessageChagned(false);
                      }
                    }
                  });

                  return (
                    <form className={styles.textField} onSubmit={handleSubmit}>
                      <div
                        className={cn(styles.bns3_input_container, {
                          [styles['short-width-field']]: isMessageChanged,
                          [styles['full-width-field']]: !isMessageChanged,
                        })}
                      >
                        <TextField
                          id="text"
                          name="text"
                          placeholder={
                            isDeletedLot
                              ? 'This lot was deleted. Sending messages is no longer possible.'
                              : 'Write a message…'
                          }
                          onChange={handleMessage}
                          value={values.text}
                          variant="standard"
                          multiline
                          minRows={1}
                          maxRows={4}
                          inputProps={{ maxLength: 2000 }}
                          InputProps={{ disableUnderline: true }}
                          disabled={isDeletedLot ? true : false}
                          className={cn('col-7 ml-3 ml-sm-0 col-sm-9 col-lg-10 form-control', styles.bns3_input, {
                            'is-invalid': errors.email && touched.email,
                          })}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSubmit();
                            }
                          }}
                          error={Boolean(errors.text && touched && touched.text)}
                          helperText={
                            errors.text && touched && touched.text ? errors.text : `${values.text.length}/2000`
                          }
                        />
                      </div>
                      <IconButton
                        className={styles.bns3_send_btn}
                        type="submit"
                        size="medium"
                        disabled={
                          isDeletedLot || !isMessageChanged || errors.text === MAX_LENGTH || !stripInvisibleChars(values.text)
                        }
                      >
                        <TelegramIcon className={styles.send_icon_btn} />
                      </IconButton>
                    </form>
                  );
                }}
              </Formik>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default observer(Conversations);
